<template>
  <div class="pagina-landing">
    <!-- PORTADA -->
    <section class="portada">
      <img
        :src="require('@/assets/logo.png')"
        alt="Bahiol"
        class="logo"
      >
      <h1 class="titulo">Publica tu negocio en Bahiol</h1>
      <p class="bajada">
        Cuatro pasos y tu empresa queda visible. Por {{ precioMesEnPesos }} COP al mes.
      </p>
    </section>

    <section class="tarjeta">
      <!-- INDICADOR DE PASOS -->
      <div class="pasos">
        <div
          v-for="(paso, indice) in pasos"
          :key="paso.clave"
          class="paso"
          :class="{
            actual: indice === pasoActual,
            hecho: indice < pasoActual
          }"
        >
          <div class="marca">
            <v-icon
              v-if="indice < pasoActual"
              size="15"
              color="#fff"
            >
              mdi-check
            </v-icon>
            <template v-else>{{ indice + 1 }}</template>
          </div>
          <span class="nombre-paso">{{ paso.nombre }}</span>
        </div>
      </div>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ error }}
      </v-alert>

      <!-- CARRUSEL DE PASOS -->
      <v-window
        v-model="pasoActual"
        class="ventana"
      >
        <!-- ---------- 1. CUENTA ---------- -->
        <v-window-item :value="0">
          <div v-if="yaTieneSesion" class="aviso-sesion">
            <v-icon color="green">mdi-check-circle</v-icon>
            <span>Ya tienes sesión iniciada. Continúa al siguiente paso.</span>
          </div>

          <template v-else>
            <v-btn-toggle
              v-model="modoCuenta"
              mandatory
              color="primary"
              density="comfortable"
              class="selector-modo"
            >
              <v-btn value="registro">Crear cuenta</v-btn>
              <v-btn value="login">Ya tengo cuenta</v-btn>
            </v-btn-toggle>

            <!-- Registro -->
            <template v-if="modoCuenta === 'registro'">
              <div class="grid-dos">
                <v-text-field
                  v-model="nombre"
                  class="fg-blue"
                  label="Nombres"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="errorNombre"
                />
                <v-text-field
                  v-model.number="edad"
                  class="fg-blue"
                  label="Edad"
                  type="number"
                  min="18"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="errorEdad"
                />
              </div>

              <div class="fila-codigo">
                <v-text-field
                  v-model="correo"
                  class="fg-blue"
                  label="Correo"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="errorCorreo"
                />
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="large"
                  class="btn-codigo"
                  :loading="enviandoCodigo"
                  :disabled="!correoValido || esperaReenvio > 0"
                  @click="pedirCodigo"
                >
                  {{ textoBotonCodigo }}
                </v-btn>
              </div>

              <v-text-field
                v-model="codigo"
                class="fg-blue"
                label="Código que te llegó al correo"
                variant="outlined"
                density="comfortable"
                maxlength="6"
                inputmode="numeric"
                placeholder="000000"
                :disabled="!codigoEnviado"
                :error-messages="errorCodigo"
              />

              <v-alert
                v-if="avisoCodigo"
                type="success"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{ avisoCodigo }}
              </v-alert>

              <div class="grid-dos">
                <v-text-field
                  v-model="usuario"
                  class="fg-blue"
                  label="Usuario"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="errorUsuario"
                />
                <v-text-field
                  v-model="clave"
                  class="fg-blue"
                  label="Contraseña"
                  variant="outlined"
                  density="comfortable"
                  :type="verClave ? 'text' : 'password'"
                  :append-inner-icon="verClave ? 'mdi-eye-off' : 'mdi-eye'"
                  :error-messages="errorClave"
                  @click:append-inner="verClave = !verClave"
                />
              </div>
            </template>

            <!-- Login -->
            <template v-else>
              <v-text-field
                v-model="usuario"
                class="fg-blue"
                label="Usuario o correo"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account-outline"
                :error-messages="errorUsuarioLogin"
              />
              <v-text-field
                v-model="clave"
                class="fg-blue"
                label="Contraseña"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-lock-outline"
                :type="verClave ? 'text' : 'password'"
                :append-inner-icon="verClave ? 'mdi-eye-off' : 'mdi-eye'"
                :error-messages="errorClaveLogin"
                @click:append-inner="verClave = !verClave"
              />
            </template>
          </template>
        </v-window-item>

        <!-- ---------- 2. EMPRESA ---------- -->
        <v-window-item :value="1">
          <div v-if="idEmpresa" class="aviso-sesion">
            <v-icon color="green">mdi-check-circle</v-icon>
            <span>Empresa "{{ empresa }}" registrada.</span>
          </div>

          <template v-else>
            <v-text-field
              v-model="empresa"
              class="fg-blue"
              label="Nombre de la empresa"
              variant="outlined"
              density="comfortable"
              :error-messages="errorEmpresa"
            />
            <v-textarea
              v-model="descripcion"
              class="fg-blue"
              label="¿A qué se dedica?"
              variant="outlined"
              density="comfortable"
              rows="3"
              :counter="1200"
              maxlength="1200"
              :error-messages="errorDescripcion"
            />
            <div class="grid-dos">
              <v-text-field
                v-model="ciudad"
                class="fg-blue"
                label="Ciudad"
                variant="outlined"
                density="comfortable"
                :error-messages="errorCiudad"
              />
              <v-text-field
                v-model="pais"
                class="fg-blue"
                label="País"
                variant="outlined"
                density="comfortable"
              />
            </div>
            <v-text-field
              v-model="telefono"
              class="fg-blue"
              label="Teléfono de contacto"
              type="tel"
              variant="outlined"
              density="comfortable"
              :error-messages="errorTelefono"
            />
          </template>
        </v-window-item>

        <!-- ---------- 3. PRODUCTOS ---------- -->
        <v-window-item :value="2">
          <p class="nota">
            Agrega lo que vendes. Puedes añadir más después, o saltar este paso
            y hacerlo luego desde tu empresa.
          </p>

          <div
            v-for="(producto, indice) in productos"
            :key="indice"
            class="producto"
          >
            <div class="cabecera-producto">
              <span>Producto {{ indice + 1 }}</span>
              <v-btn
                icon
                variant="text"
                size="x-small"
                color="red"
                @click="quitarProducto(indice)"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="grid-dos">
              <v-text-field
                v-model="producto.nombre"
                class="fg-blue"
                label="Nombre"
                variant="outlined"
                density="compact"
                hide-details
              />
              <v-text-field
                v-model.number="producto.precio"
                class="fg-blue"
                label="Precio (COP)"
                type="number"
                min="0"
                variant="outlined"
                density="compact"
                hide-details
              />
            </div>
            <v-text-field
              v-model="producto.descripcion"
              class="fg-blue mt-2"
              label="Descripción corta"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>

          <v-btn
            variant="tonal"
            size="small"
            class="mt-1"
            @click="agregarProducto"
          >
            <v-icon start>mdi-plus</v-icon>
            Agregar otro producto
          </v-btn>
        </v-window-item>

        <!-- ---------- 4. PAGO ---------- -->
        <v-window-item :value="3">
          <div class="resumen">
            <div class="fila-resumen">
              <span>Empresa</span>
              <strong>{{ empresa }}</strong>
            </div>
            <div class="fila-resumen">
              <span>Productos</span>
              <strong>{{ productosGuardados }}</strong>
            </div>
            <div class="fila-resumen total">
              <span>Un mes de visibilidad</span>
              <strong>{{ precioMesEnPesos }} COP</strong>
            </div>
          </div>

          <p class="nota">
            Al continuar se abre la pasarela de pagos. Tu empresa queda visible
            apenas se apruebe el pago.
          </p>
        </v-window-item>
      </v-window>

      <!-- NAVEGACIÓN -->
      <div class="navegacion">
        <v-btn
          v-if="pasoActual > 0"
          variant="text"
          :disabled="procesando"
          @click="pasoActual--"
        >
          Atrás
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="pasoActual === 2 && productosGuardados === 0"
          variant="text"
          :disabled="procesando"
          @click="pasoActual = 3"
        >
          Saltar
        </v-btn>
        <v-btn
          color="primary"
          size="large"
          :loading="procesando"
          @click="continuar"
        >
          {{ textoBoton }}
        </v-btn>
      </div>

      <div
        v-if="progreso"
        class="progreso"
      >
        {{ progreso }}
      </div>

      <div class="pie">
        ¿Prefieres hacerlo por separado?
        <a
          class="enlace"
          @click="$router.push({ name: 'LoginView' })"
        >Inicia sesión</a>
      </div>
    </section>
  </div>
</template>

<script>
import apisUsers from '@/apis/apisUsers'
import apisCompanys from '@/apis/apisCompanys'
import apisProducts from '@/apis/apisProducts'
import apisPAYS from '@/apis/apisPAYS'
import { confirmarPagoWompi } from '@/utils/pagoWompi'
import { precioMesHospedaje, enPesos, PRECIO_MES_RESPALDO } from '@/utils/configuracion'

// Las mismas reglas que aplica UserController.save. Se repiten aqui para que
// el usuario no las descubra de a una con errores del servidor.
const REQUERIDO = v => (v !== null && v !== undefined && String(v).trim() !== '') || 'Campo obligatorio'
const CORREO = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Correo no válido'
const MAYOR_DE_EDAD = v => Number(v) >= 18 || 'Debes ser mayor de 18 años'
const CLAVE = v => (String(v || '').length >= 5 && /[A-Za-z]/.test(v || '') && /\d/.test(v || ''))
  || 'Mínimo 5 caracteres, con al menos una letra y un número'
const USUARIO = v => /^[a-zA-Z0-9_]{4,20}$/.test(v || '')
  || 'Entre 4 y 20 caracteres: letras, números o guion bajo'

function productoVacio() {
  return { nombre: '', precio: null, descripcion: '' }
}

export default {
  name: 'LandingEmpezarView',
  data() {
    return {
      // Lo administra Settings. Arranca en el respaldo para que el resumen del
      // paso de pago no salga vacio mientras llega la respuesta.
      precioMes: PRECIO_MES_RESPALDO,
      pasoActual: 0,
      pasos: [
        { clave: 'cuenta', nombre: 'Cuenta' },
        { clave: 'empresa', nombre: 'Empresa' },
        { clave: 'productos', nombre: 'Productos' },
        { clave: 'pago', nombre: 'Pago' }
      ],

      // Cuenta
      modoCuenta: 'registro',
      yaTieneSesion: false,
      nombre: '',
      correo: '',
      edad: null,
      usuario: '',
      clave: '',
      verClave: false,

      // Codigo de verificacion
      codigo: '',
      codigoEnviado: false,
      correoDelCodigo: '',
      enviandoCodigo: false,
      avisoCodigo: '',
      esperaReenvio: 0,
      temporizador: null,

      // Empresa
      empresa: '',
      descripcion: '',
      ciudad: '',
      pais: 'Colombia',
      telefono: '',
      idEmpresa: null,

      // Productos
      productos: [productoVacio()],
      productosGuardados: 0,

      procesando: false,
      intentoEnvio: false,
      error: '',
      progreso: ''
    }
  },
  computed: {
    precioMesEnPesos() { return enPesos(this.precioMes) },
    errorNombre() { return this.mensaje(REQUERIDO(this.nombre)) },
    errorCorreo() { return this.mensaje(REQUERIDO(this.correo), CORREO(this.correo)) },
    errorEdad() { return this.mensaje(REQUERIDO(this.edad), MAYOR_DE_EDAD(this.edad)) },
    errorUsuario() { return this.mensaje(REQUERIDO(this.usuario), USUARIO(this.usuario)) },
    errorClave() { return this.mensaje(REQUERIDO(this.clave), CLAVE(this.clave)) },
    errorCodigo() { return this.mensaje(REQUERIDO(this.codigo)) },
    // En login no se validan formato ni longitud: la cuenta ya existe y sus
    // reglas pudieron cambiar. Solo que no vengan vacios.
    errorUsuarioLogin() { return this.mensaje(REQUERIDO(this.usuario)) },
    errorClaveLogin() { return this.mensaje(REQUERIDO(this.clave)) },
    errorEmpresa() { return this.mensaje(REQUERIDO(this.empresa)) },
    errorDescripcion() { return this.mensaje(REQUERIDO(this.descripcion)) },
    errorCiudad() { return this.mensaje(REQUERIDO(this.ciudad)) },
    errorTelefono() { return this.mensaje(REQUERIDO(this.telefono)) },

    correoValido() {
      return CORREO(this.correo) === true
    },
    textoBotonCodigo() {
      if (this.esperaReenvio > 0) {
        return `Reenviar en ${this.esperaReenvio}s`
      }
      return this.codigoEnviado ? 'Reenviar' : 'Enviar código'
    },
    textoBoton() {
      if (this.pasoActual === 3) return 'Ir a pagar'
      return 'Continuar'
    }
  },
  watch: {
    // El codigo esta atado al correo al que se envio; si cambia, ya no sirve
    correo(nuevo) {
      if (this.codigoEnviado && nuevo.trim().toLowerCase() !== this.correoDelCodigo) {
        this.codigoEnviado = false
        this.codigo = ''
        this.avisoCodigo = ''
        clearInterval(this.temporizador)
        this.esperaReenvio = 0
      }
    },
    // Cada paso empieza limpio: los errores del anterior ya no aplican
    pasoActual() {
      this.intentoEnvio = false
      this.error = ''
    }
  },
  created() {
    precioMesHospedaje().then(precio => {
      this.precioMes = precio
    })

    // Si llega con sesion abierta, pedirle datos de cuenta otra vez solo
    // lograria que el registro falle por usuario repetido
    apisUsers.isValid()
      .then(valido => {
        this.yaTieneSesion = !!valido
      })
      .catch(() => {
        this.yaTieneSesion = false
      })
  },
  beforeUnmount() {
    clearInterval(this.temporizador)
  },
  methods: {
    mensaje(...resultados) {
      if (!this.intentoEnvio) {
        return []
      }
      const fallo = resultados.find(r => r !== true)
      return fallo ? [fallo] : []
    },

    agregarProducto() {
      this.productos.push(productoVacio())
    },
    quitarProducto(indice) {
      this.productos.splice(indice, 1)
      if (this.productos.length === 0) {
        this.productos.push(productoVacio())
      }
    },

    pedirCodigo() {
      this.error = ''
      this.avisoCodigo = ''
      this.enviandoCodigo = true

      const correo = this.correo.trim().toLowerCase()

      apisUsers.solicitarCodigo(correo, 'registro')
        .then(() => {
          this.codigoEnviado = true
          this.correoDelCodigo = correo
          this.avisoCodigo = `Te enviamos un código a ${correo}. Revisa también la carpeta de spam.`
          this.iniciarEsperaReenvio()
        })
        .catch(err => {
          this.error = err.message
        })
        .finally(() => {
          this.enviandoCodigo = false
        })
    },
    /** El backend rechaza otro codigo antes de 60s; se refleja en el boton. */
    iniciarEsperaReenvio() {
      clearInterval(this.temporizador)
      this.esperaReenvio = 60

      this.temporizador = setInterval(() => {
        this.esperaReenvio--
        if (this.esperaReenvio <= 0) {
          clearInterval(this.temporizador)
        }
      }, 1000)
    },

    /** Cada paso hace su trabajo y solo avanza si salio bien. */
    async continuar() {
      this.error = ''
      this.intentoEnvio = true
      this.procesando = true

      try {
        if (this.pasoActual === 0) await this.pasoCuenta()
        else if (this.pasoActual === 1) await this.pasoEmpresa()
        else if (this.pasoActual === 2) await this.pasoProductos()
        else await this.pasoPago()

      } catch (err) {
        this.error = err.message || 'Algo salió mal. Intenta de nuevo.'
      } finally {
        this.procesando = false
        this.progreso = ''
      }
    },

    async pasoCuenta() {
      if (this.yaTieneSesion) {
        this.pasoActual = 1
        return
      }

      if (this.modoCuenta === 'login') {
        if ([REQUERIDO(this.usuario), REQUERIDO(this.clave)].some(r => r !== true)) {
          throw new Error('Escribe tu usuario y tu contraseña')
        }

        this.progreso = 'Iniciando sesión...'
        const entro = await apisUsers.login(this.usuario.trim(), this.clave)

        if (!entro) {
          throw new Error('Usuario, correo o contraseña incorrectos')
        }
      } else {
        const fallos = [REQUERIDO(this.nombre), REQUERIDO(this.correo), CORREO(this.correo),
          REQUERIDO(this.edad), MAYOR_DE_EDAD(this.edad), REQUERIDO(this.usuario),
          USUARIO(this.usuario), REQUERIDO(this.clave), CLAVE(this.clave),
          REQUERIDO(this.codigo)].filter(r => r !== true)

        if (fallos.length > 0) {
          throw new Error('Revisa los datos marcados')
        }

        this.progreso = 'Creando tu cuenta...'
        await apisUsers.save(
          this.usuario.trim(), this.clave, this.nombre.trim(), '',
          this.correo.trim().toLowerCase(), this.edad, '', '',
          this.codigo.trim()
        )

        this.progreso = 'Iniciando sesión...'
        const entro = await apisUsers.login(this.usuario.trim(), this.clave)

        if (!entro) {
          throw new Error('Tu cuenta quedó creada, pero no pudimos iniciar sesión. Entra desde la pantalla de acceso.')
        }
      }

      this.yaTieneSesion = true
      this.pasoActual = 1
    },

    async pasoEmpresa() {
      // Ya creada: si el usuario volvio atras, no se duplica
      if (this.idEmpresa) {
        this.pasoActual = 2
        return
      }

      const fallos = [REQUERIDO(this.empresa), REQUERIDO(this.descripcion),
        REQUERIDO(this.ciudad), REQUERIDO(this.telefono)].filter(r => r !== true)

      if (fallos.length > 0) {
        throw new Error('Revisa los datos marcados')
      }

      this.progreso = 'Registrando tu empresa...'
      const creada = await apisCompanys.saveCompany({
        company: this.empresa.trim(),
        country: this.pais.trim(),
        city: this.ciudad.trim(),
        description: this.descripcion.trim(),
        location: '',
        phone: this.telefono.trim()
      })

      if (!creada || !creada.idCompany) {
        throw new Error('No pudimos registrar la empresa. Revisa que hayas iniciado sesión.')
      }

      this.idEmpresa = creada.idCompany
      this.pasoActual = 2
    },

    async pasoProductos() {
      // Solo cuentan los que tienen nombre: las filas vacias se ignoran
      const conNombre = this.productos.filter(p => p.nombre && p.nombre.trim() !== '')

      if (conNombre.length === 0) {
        this.pasoActual = 3
        return
      }

      this.progreso = `Guardando ${conNombre.length} producto(s)...`

      // De a uno y no en paralelo, para no disparar una rafaga al servidor
      let guardados = 0
      for (const p of conNombre) {
        await apisProducts.saveProduct(
          this.idEmpresa, p.nombre.trim(), '', (p.descripcion || '').trim(), p.precio || 0
        )
        guardados++
      }

      this.productosGuardados = guardados
      // Se vacia para que volver atras y continuar no los duplique
      this.productos = [productoVacio()]
      this.pasoActual = 3
    },

    /**
     * Widget incrustado de Wompi, exactamente igual que MyPaymentsView. Aqui no
     * se toca nada del flujo de pagos: solo se llama al mismo endpoint que ya
     * existia y se abre el mismo widget.
     */
    async pasoPago() {
      this.progreso = 'Abriendo la pasarela de pago...'
      const datos = await apisPAYS.payOneMonth(this.idEmpresa)

      if (!window.WidgetCheckout) {
        throw new Error('No se pudo cargar la pasarela de pagos. Recarga la página.')
      }

      const checkout = new window.WidgetCheckout({
        currency: datos.currency,
        amountInCents: datos.amountInCents,
        reference: datos.reference,
        publicKey: datos.publicKey,
        signature: { integrity: datos.signature }
      })

      checkout.open(async resultado => {
        const { estado } = await confirmarPagoWompi(resultado)

        // En proceso (PSE, Bancolombia...) tambien sigue: ya en su empresa ve
        // cuando se active
        if (estado === 'APPROVED' || estado === 'PENDING') {
          this.$router.push({ name: 'EditCompanyView', params: { id: this.idEmpresa } })
        } else {
          // La empresa ya existe: solo falta el pago, y el boton lo reintenta
          this.error = 'El pago no se completó. Tu empresa quedó registrada, puedes reintentar el pago.'
        }
      })
    }
  }
}
</script>

<style scoped>
.pagina-landing {
  background: linear-gradient(160deg, #dbeafe 0%, #eff6ff 45%, #f8fafc 100%);
  min-height: 100%;
  padding-bottom: 32px;
}

/* PORTADA */
.portada {
  background: linear-gradient(160deg, #0d2f59 0%, #1976d2 100%);
  color: #fff;
  padding: 30px 20px 40px;
  text-align: center;
}
.logo {
  height: 44px;
  margin-bottom: 12px;
}
.titulo {
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 6px;
}
.bajada {
  font-size: 0.93rem;
  line-height: 1.5;
  opacity: 0.9;
  margin: 0 auto;
  max-width: 420px;
}

/* TARJETA */
.tarjeta {
  max-width: 560px;
  margin: -22px auto 0;
  background: #fff;
  border-radius: 16px;
  padding: 20px 18px;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.12);
}

/* INDICADOR DE PASOS */
.pasos {
  display: flex;
  gap: 4px;
  margin-bottom: 18px;
}
.paso {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border-top: 3px solid #e3e8ef;
  padding-top: 8px;
}
.paso.actual {
  border-top-color: #1976d2;
}
.paso.hecho {
  border-top-color: #2e7d32;
}
.marca {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e3e8ef;
  color: #7a8ba0;
  font-size: 0.75rem;
  font-weight: 700;
}
.paso.actual .marca {
  background: #1976d2;
  color: #fff;
}
.paso.hecho .marca {
  background: #2e7d32;
  color: #fff;
}
.nombre-paso {
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #7a8ba0;
}
.paso.actual .nombre-paso {
  color: #1976d2;
}

/* v-window recorta por defecto y corta las sombras de los campos */
.ventana {
  overflow: visible;
}

.selector-modo {
  width: 100%;
  margin-bottom: 16px;
}
.selector-modo :deep(.v-btn) {
  flex: 1;
}

.aviso-sesion {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e8f5e9;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.88rem;
  color: #2e7d32;
}

/* Una columna en celular: dos campos por fila aprietan */
.grid-dos {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 10px;
}

.fila-codigo {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
}
.btn-codigo {
  margin-top: 4px;
  white-space: nowrap;
}

/* PRODUCTOS */
.producto {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
}
.cabecera-producto {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #7a8ba0;
  margin-bottom: 8px;
}

/* PAGO */
.resumen {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 14px;
}
.fila-resumen {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.9rem;
  padding: 5px 0;
  color: #555;
}
.fila-resumen.total {
  border-top: 1px solid #e3e8ef;
  margin-top: 6px;
  padding-top: 10px;
  font-size: 1rem;
  color: #111;
}

.nota {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.45;
  margin: 0 0 12px;
}

.navegacion {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
}

.progreso {
  text-align: center;
  font-size: 0.83rem;
  color: #1976d2;
  margin-top: 10px;
}

.pie {
  margin-top: 16px;
  text-align: center;
  font-size: 0.85rem;
  color: #666;
}
.enlace {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  margin-left: 2px;
}
.enlace:hover {
  text-decoration: underline;
}

@media (min-width: 700px) {
  .portada {
    padding: 42px 20px 52px;
  }
  .titulo {
    font-size: 1.9rem;
  }
  .tarjeta {
    max-width: 620px;
    padding: 26px 32px;
  }
  .grid-dos {
    grid-template-columns: 1fr 1fr;
  }
  .nombre-paso {
    font-size: 0.72rem;
  }
}
</style>
